<template>
  <VCard>
    <VCardText class="pa-0">
      <VTabs v-model="activeTab" fixed-tabs>
        <VTab value="calendar">
          <slot name="dateIcon">
            <VIcon>{{ icons.mdiCalendar }}</VIcon>
          </slot>
        </VTab>
        <VTab value="timer" :disabled="dateSelected">
          <slot name="timeIcon">
            <VIcon>{{ icons.mdiClock }}</VIcon>
          </slot>
        </VTab>
      </VTabs>
      <VTabsWindow v-model="activeTab">
        <VTabsWindowItem value="calendar">
          <VDatePicker v-model="date" @update:model-value="showTimePicker" />
        </VTabsWindowItem>
        <VTabsWindowItem value="timer">
          <VTimePicker
            ref="timer"
            v-model="time"
            class="v-time-picker-custom"
          />
        </VTabsWindowItem>
      </VTabsWindow>
    </VCardText>
    <VCardActions>
      <VSpacer />
      <slot name="actions">
        <VBtn color="grey lighten-1" small text data-testid="clear-time-picker" @click="clearHandler">
          clear
        </VBtn>
        <VBtn color="green darken-1" large text data-testid="submit-date-time" @click="okHandler">
          ok
        </VBtn>
      </slot>
    </VCardActions>
  </VCard>
</template>


<script lang="ts" setup>
import { mdiCalendar, mdiClock } from '@mdi/js'
import { toLocalYmd } from '~/utils/format/utc-date'

const emits = defineEmits<{
  (e: 'input', value: string | null | undefined): void
}>()
const DEFAULT_DATE = ''
const DEFAULT_TIME = '00:00:00'
const icons = { mdiCalendar, mdiClock }

const display = ref(false)
const activeTab = ref('calendar')
const date = ref<string | Date>(DEFAULT_DATE)
const time = ref(DEFAULT_TIME)

const timer = ref()

// The calendar day is read in local time via toLocalYmd: VDatePicker yields local
// midnight, so toISOString() would roll it back a day in browsers east of UTC.
const normalizedDate = computed(() => {
  if (!date.value) {
    return null
  }
  if (date.value instanceof Date) {
    return toLocalYmd(date.value)
  }
  if (typeof date.value === 'string') {
    if (/^\d{4}-\d{2}-\d{2}$/.test(date.value)) {
      return date.value
    }
    const parsed = new Date(date.value)
    if (!Number.isNaN(parsed.getTime())) {
      // A string carrying its own zone is an instant: keep its UTC calendar day.
      return parsed.toISOString().slice(0, 10)
    }
  }
  return null
})

const normalizedTime = computed(() => {
  if (!time.value) {
    return null
  }
  const [hours = '00', minutes = '00', seconds = '00'] = time.value.split(':')
  return `${hours}:${minutes}:${seconds}`
})

const selectedDatetime = computed(() => {
  if (!normalizedDate.value || !normalizedTime.value) {
    return null
  }
  return `${normalizedDate.value}T${normalizedTime.value}Z`
})

const dateSelected = computed(() => {
  return !date.value
})

const resetPicker = () => {
  display.value = false
  activeTab.value = 'calendar'
  if (timer.value) {
    timer.value.selectingHour = true
  }
}
const okHandler = () => {
  resetPicker()
  emits('input', selectedDatetime.value)
}

const clearHandler = () => {
  resetPicker()
  date.value = DEFAULT_DATE
  time.value = DEFAULT_TIME
  emits('input', null)
}

const showTimePicker = () => {
  activeTab.value = 'timer'
}

</script>
